import React from 'react';
import {view} from '@sail/ui';
import {defineMessages, useIntl} from 'react-intl';
import type {Wallet} from '@stripe-internal/wallet-connect-wrapper';
import {getStore} from 'src/v2/state/store';
import {ChipButton} from 'src/v2/components/common';
import {wallet as walletIcon} from '@sail/icons/react/Icon';
import {Banner} from 'src/v2/components/common/Banner';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {WalletItem} from 'src/v2/components/walletSwitch/WalletItem';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {getWalletNetworkSupport} from 'src/v2/components/wallet/getWalletNetworkSupport';
import {useLogger} from 'src/utils/logger';
import {getReadableNetworkType} from 'src/components/Wallet/constants';

const messages = defineMessages({
  connectNewWallet: {
    id: 'crypto.payins.v2.wallet_switch.connect_new_wallet',
    description: 'Button text to connect a new wallet',
    defaultMessage: 'Connect a new wallet',
  },
  unsupportedWalletsWithOptions: {
    id: 'crypto.payins.v2.wallet_switch.unsupported_wallets_with_options',
    description:
      'Error message when wallets of a certain type are unsupported but other wallets exist',
    defaultMessage:
      "We don't accept {networkType} wallets for this purchase. Choose a different wallet or connect a new one to continue.",
  },
  unsupportedWalletsConnectOnly: {
    id: 'crypto.payins.v2.wallet_switch.unsupported_wallets_connect_only',
    description:
      'Error message when wallets of a certain type are unsupported and no other wallets exist',
    defaultMessage:
      "We don't accept {networkType} wallets for this purchase. Connect a new one to continue.",
  },
});

export const WalletSwitchScreen = () => {
  const store = getStore();
  const walletClient = useWalletConnectionClient();
  const [updateSessionOnWalletConnect] = useUpdateSessionOnWalletConnect();
  const {mode} = useSessionContext();
  const logger = useLogger();
  const intl = useIntl();

  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const connectedWallets = store.useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );

  // Sort wallets with primary (active) wallet first
  const sortedWallets = React.useMemo(() => {
    if (!activeWallet) {
      return [];
    }

    // Filter out the primary wallet first
    const otherWallets = connectedWallets.filter(
      (w) => w.id !== activeWallet.id,
    );

    // Prepend the primary wallet to the list
    return [activeWallet, ...otherWallets];
  }, [connectedWallets, activeWallet]);

  if (!activeWallet) {
    // Redirect to wallet select screen if no active wallet
    logger.log('crypto_payin_session.error', {
      error_code: 'fallback_due_to_missing_active_wallet',
    });
    store.setValue((state) => {
      state.screen = 'WALLET_SELECT';
    });
    return null;
  }

  const unsupportedWallets = connectedWallets.filter((wallet) => {
    const networkSupport = getWalletNetworkSupport(wallet, supportedCurrencies);
    return networkSupport === 'not_supported';
  });

  let unsupportedWalletMessage;
  if (unsupportedWallets.length > 0) {
    // Assume either Solana or EVM is supported
    const networkType = getReadableNetworkType(unsupportedWallets[0].network);
    const message =
      connectedWallets.length - unsupportedWallets.length > 0
        ? messages.unsupportedWalletsWithOptions
        : messages.unsupportedWalletsConnectOnly;
    unsupportedWalletMessage = intl.formatMessage(message, {
      networkType,
    });
  }

  const handleWalletSelect = async (wallet: Wallet) => {
    try {
      // Use the new switchWallet method to properly switch wallets
      await walletClient.switchWallet(wallet.id);

      // Update session on wallet connect after successful wallet switch
      await updateSessionOnWalletConnect({
        walletAddress: wallet.address,
        network: wallet.network,
        type: wallet.type,
        walletKey: wallet.key,
      });

      const networkSupport = getWalletNetworkSupport(
        wallet,
        supportedCurrencies,
      );

      if (networkSupport === 'supported') {
        // Navigate back to PAY or SETUP screen based on session mode
        store.setValue((state) => {
          state.screen = mode === 'setup' ? 'SETUP' : 'PAY';
        });
      } else {
        // Network type (evm) is supported, so navigate to network switch screen
        store.setValue((state) => {
          state.screen = 'NETWORK_SWITCH';
        });
      }
    } catch (error) {
      // TODO: Handle error (show toast, etc.)
      console.error('Failed to switch wallet:', error);
    }
  };

  const handleConnectNewWallet = () => {
    store.setValue((state) => {
      state.screen = 'WALLET_SELECT';
    });
  };

  return (
    <view.div css={{stack: 'y', gapY: 'medium'}}>
      <view.div css={{stack: 'y', gapY: 'space.150'}}>
        {sortedWallets.map((wallet) => {
          const networkSupport = getWalletNetworkSupport(
            wallet,
            supportedCurrencies,
          );
          const unsupportedNetworkType = networkSupport === 'not_supported';

          const walletItem = (
            <WalletItem
              key={wallet.id}
              wallet={wallet}
              isActive={activeWallet?.id === wallet.id}
              onClick={() => handleWalletSelect(wallet)}
              disabled={unsupportedNetworkType}
            />
          );

          return walletItem;
        })}
      </view.div>

      {unsupportedWalletMessage ? (
        <Banner message={unsupportedWalletMessage} variant="error" />
      ) : null}

      <ChipButton
        icon={walletIcon}
        onClick={handleConnectNewWallet}
        label={intl.formatMessage(messages.connectNewWallet)}
        css={{alignSelfX: 'center'}}
      />
    </view.div>
  );
};
